import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "redux/store";
import { useFormik } from "formik";
import {
  getallupdateProperties,
  updateProperties,
} from "../../redux/reducers/Properties/PropertiesSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import CommonLayoutV2 from "./CommonLayoutV2";
import CheckboxV2 from "shared/Checkbox/CheckboxV2";
import FormItem from "./FormItem";
import ReactDate from "shared/DateField/ReactDate";
import moment from "moment";
import CustomDatePickerV2 from "shared/DateField/CustomDatePickerV2";
import { calculateStepCompletion, checkExistingCompletion } from "utils/helpers/listingCompletionHelper";
import * as Yup from "yup";
import "../../../src/shared/DateField/CustomDatePicker.scss";
import { toast } from "react-toastify";
import { captureError } from "utils/sentry";

export interface ListingCalenderProps {}

const ListingCalender: FC<ListingCalenderProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { PropertyToEdit } = useSelector((state: RootState) => state.property);
  const { slug } = useParams();
  const id = slug;
  const [excludedDates, setExcludedDates] = useState<{ start: Date; end: Date }[]>([]);
  const RouteID = id;
  const [minimumMonth, setMinimumMonth] = useState<any>(2);
  const [isSharedProperty, setIsSharedProperty] = useState(false);
  const [initialValues, setInitialValues] = useState<{
    availability_start: string | null;
    availability_end: string | null;
    unlimited_availability: boolean;
    minimum_months_of_booking: number;
  }>({
    availability_start: null,
    availability_end: null,
    unlimited_availability: false,
    minimum_months_of_booking: 0,
  });

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false, fieldsCompleted: 0, totalFields: 2 },
    8: { completed: false },
    9: { completed: false },
  });

  // Track form values for completion
  const [formValues, setFormValues] = useState<any>(null);
  const [formErrors, setFormErrors] = useState<any>({});

  interface FormValues {
    minimum_months_of_booking: number;
    availability_start: string | null;
    availability_end: string | null;
    unlimited_availability: boolean;
  }

  const validationSchema = Yup.object().shape({
    availability_start: Yup.string().required("Availability start date is required"),
    availability_end: Yup.string().when(
      "unlimited_availability",
      ([unlimited_availability], schema) => {
        return unlimited_availability
          ? schema.nullable()
          : schema.required("Availability end date is required");
      }
    ),
    unlimited_availability: Yup.boolean(),
  });

  const isEditing = (currentValues: FormValues) => {
    return Object.entries(currentValues).some(([key, value]) => {
      return value !== initialValues[key as keyof FormValues];
    });
  };

  const formik = useFormik<FormValues>({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      if (!isEditing(values)) {
        navigate(`/add-listing-8/${slug}`);
        return;
      }

      const payload: any = {
        id: id,
        availability_start: moment(values.availability_start).format("YYYY-MM-DD"),
        availability_end: values.unlimited_availability
          ? null
          : moment(values.availability_end).format("YYYY-MM-DD"),
        unlimited_availability: values.unlimited_availability,
      };

      setSubmitting(true);
      dispatch(updateProperties({ property: payload }))
        .unwrap()
        .then((response: any) => {
  if (response) {
    toast.success(response?.message || "Updated successfully!");
    resetForm();
    navigate(`/add-listing-8/${slug}`);
  }
})
.catch((error: any) => {
  toast.error("Failed to save availability. Please try again.");
  captureError(error, { stage: "calendar_update", propertyId: id });
})
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    const propertyId = id;
    if (propertyId) {
      dispatch(getallupdateProperties({ propertyId }))
        .unwrap()
        .then((response: any) => {
          if (response.success && response.property) {
            const {
              availability_start,
              availability_end,
              minimum_months_of_booking,
              unlimited_availability,
              property_type,
            } = response.property;

            // Check if property type is "Shared Property"
            const isShared = property_type?.name === "Shared Property";
            setIsSharedProperty(isShared);

            setMinimumMonth(minimum_months_of_booking);

            if (isShared) {
              // For shared properties, set default values and skip to next step
              const todayFormatted = moment().format("YYYY-MM-DD");
              
              // Update backend if not already configured
              if (!availability_start || !unlimited_availability) {
                dispatch(updateProperties({ 
                  property: {
                    id: id,
                    availability_start: todayFormatted,
                    availability_end: null,
                    unlimited_availability: true,
                  }
                }))
                .unwrap()
                .then(() => {
                  // Redirect to next step immediately after saving
                  navigate(`/add-listing-8/${slug}`);
                })
                .catch((error: any) => {
                  captureError(error, { stage: "calendar_shared_property_update", propertyId: id });
                  navigate(`/add-listing-8/${slug}`);
                });
              } else {
                // Already configured, redirect immediately
                navigate(`/add-listing-8/${slug}`);
              }
            } else {
              // For entire properties, use existing values
              const newValues = {
                availability_start,
                availability_end,
                unlimited_availability,
                minimum_months_of_booking: minimum_months_of_booking || 0,
              };
              setInitialValues(newValues);
              formik.setValues(newValues);
            }

            if (minimum_months_of_booking) {
              setMinimumMonth(minimum_months_of_booking);
            } else {
              setMinimumMonth(1);
            }
          }
        })
        .catch((error: any) => {
          toast.error("Failed to load property data. Please refresh.");
          captureError(error, { stage: "calendar_fetch", propertyId: id });
          setMinimumMonth(1);
        });
    }
  }, [dispatch, id, navigate, slug]);

  // Calculate completion for Step 7
  useEffect(() => {
    if (formValues) {
      const requiredFields = formValues.unlimited_availability
        ? ["availability_start"]
        : ["availability_start", "availability_end"];
      const hasErrors = Object.keys(formErrors).length > 0;
      const completion = calculateStepCompletion(formValues, requiredFields, hasErrors);

      setStepCompletionStatus((prev) => ({
        ...prev,
        7: {
          ...completion,
          totalFields: formValues.unlimited_availability ? 1 : 2,
        },
      }));
    }
  }, [formValues, formErrors]);

  // Load completion status from PropertyToEdit
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);

      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  // Update form tracking
  useEffect(() => {
    if (JSON.stringify(formik.values) !== JSON.stringify(formValues)) {
      setFormValues(formik.values);
    }
    if (JSON.stringify(formik.errors) !== JSON.stringify(formErrors)) {
      setFormErrors(formik.errors);
    }
  }, [formik.values, formik.errors]);

  const handleStartDateChange = (date: any) => {
    const formattedStartDate = date.format("YYYY-MM-DD");
    formik.setFieldValue("availability_start", formattedStartDate);
    const dynamicEndDate = moment(date).add(minimumMonth, "months").endOf("day");

    formik.setFieldValue("availability_end", dynamicEndDate.format("YYYY-MM-DD"));
  };

  return (
    <CommonLayoutV2
      currentHref="/add-listing-7"
      PropertyID={RouteID}
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Header Section */}
        <div className="pb-6 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Availability Calendar
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Set when your property is available for booking. You can choose specific dates or unlimited availability.
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-8">
          {/* Date Settings Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-[#F56948] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  1
                </span>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Set Availability Dates *
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Choose when guests can book your property
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Unlimited Availability Toggle */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border border-gray-200 dark:border-gray-600">
                <CheckboxV2
                  name="unlimited_availability"
                  label={
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        Unlimited Availability
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Property is always available for booking
                      </span>
                    </div>
                  }
                  onChange={(checked) => {
                    formik.setFieldValue("unlimited_availability", checked);
                    if (checked) {
                      formik.setFieldValue("availability_end", null);
                    }
                  }}
                  checked={formik.values.unlimited_availability}
                />
              </div>

              {/* Date Pickers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormItem label="Start Date *" desc="When does availability begin?">
                  <CustomDatePickerV2
                    placeholder="Select start date"
                    value={
                      formik.values.availability_start
                        ? moment(formik.values.availability_start)
                        : null
                    }
                    onChange={(value) => {
                      formik.setFieldValue("availability_start", value);
                      formik.setFieldValue("availability_end", null);
                    }}
                    minDate={new Date()}
                  />
                  {formik.errors.availability_start &&
                    formik.touched.availability_start && (
                      <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {formik.errors.availability_start}
                      </div>
                    )}
                </FormItem>

                {!formik.values.unlimited_availability && (
                  <FormItem label="End Date *" desc="When does availability end?">
                    <CustomDatePickerV2
                      placeholder="Select end date"
                      value={
                        formik.values.availability_end
                          ? moment(formik.values.availability_end)
                          : null
                      }
                      onChange={(value) => {
                        formik.setFieldValue("availability_end", value);
                      }}
                      minDate={
                        formik.values.availability_start
                          ? moment(formik.values.availability_start).add(1, "day").toDate()
                          : new Date()
                      }
                    />
                    {!formik.values.unlimited_availability &&
                      formik.errors.availability_end &&
                      formik.touched.availability_end && (
                        <div className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {formik.errors.availability_end}
                        </div>
                      )}
                  </FormItem>
                )}
              </div>

              {/* Info Box */}
              {formik.values.availability_start && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <div className="flex gap-3">
                    <svg
                      className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                        Availability Period
                      </h4>
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        {formik.values.unlimited_availability ? (
                          <>
                            Starting from{" "}
                            <strong>
                              {moment(formik.values.availability_start).format("MMMM D, YYYY")}
                            </strong>{" "}
                            with no end date
                          </>
                        ) : formik.values.availability_end ? (
                          <>
                            From{" "}
                            <strong>
                              {moment(formik.values.availability_start).format("MMMM D, YYYY")}
                            </strong>{" "}
                            to{" "}
                            <strong>
                              {moment(formik.values.availability_end).format("MMMM D, YYYY")}
                            </strong>
                            {" "}(
                            {moment(formik.values.availability_end).diff(
                              moment(formik.values.availability_start),
                              "days"
                            )}{" "}
                            days)
                          </>
                        ) : (
                          <>Select an end date to see the availability period</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Calendar Preview Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                2
              </span>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Calendar Preview
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Visual representation of your availability
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
              <ReactDate
                availabilityStart={formik.values.availability_start}
                availabilityEnd={formik.values.availability_end}
                unlimitedAvailability={formik.values.unlimited_availability}
                excludedDates={excludedDates}
              />
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span className="text-gray-700 dark:text-gray-300">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span className="text-gray-700 dark:text-gray-300">Unavailable</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-300 dark:bg-gray-600"></div>
                <span className="text-gray-700 dark:text-gray-300">Not Set</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Step 7 of 9 • Set your availability calendar
            </div>
            <div className="flex gap-3">
              <ButtonSecondary href={`/add-listing-6/${slug}`} disabled={formik.isSubmitting}>
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Go Back
                </span>
              </ButtonSecondary>
              <ButtonPrimary type="submit" disabled={formik.isSubmitting} className="min-w-[140px]">
                {formik.isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Continue
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </span>
                )}
              </ButtonPrimary>
            </div>
          </div>
        </form>
      </div>
    </CommonLayoutV2>
  );
};

export default ListingCalender;